import React, { useCallback, useRef, useState } from 'react';

import { convertCountryStringToIsoCountryCode2Char } from '@rbilabs/common';
import { useIntl } from 'react-intl';

import { SupportedRegions } from '@rbi-ctg/frontend';
import ChangeRegionModal from 'components/language-selector-modal';
import { IStoreDiagnosticData } from 'components/store-diagnostic-modal';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { useStoreCard } from 'hooks/store-card';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { StoreState } from 'state/store';
import { routes } from 'utils/routing';

import { useOrderButtonProps } from './hooks/use-order-button-props';
import { StoreCardSlimView } from './store-card-slim.view';
import { determineDefaultOrderClickHandler } from './utils/determine-default-order-click-handler';

const getRestaurantCountry = (restaurant: IRestaurantNode) =>
  convertCountryStringToIsoCountryCode2Char(restaurant?.physicalAddress?.country || '');

const restaurantCountryMatchesRegion = (restaurant: IRestaurantNode, region: SupportedRegions) =>
  getRestaurantCountry(restaurant) === region;

export interface IStoreCardSlimProps {
  restaurant: IRestaurantNode;
  isSelected?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  onClick(): void;
  onClickRestaurantHoursInfo?: () => void;
  buttonOnly?: boolean;
  buttonTextOverride?: string;
  onButtonClickOverride?: (id?: string) => void;
  alwaysSelectable?: boolean;
}

export const StoreCardSlim: React.FC<IStoreCardSlimProps> = ({
  restaurant,
  onClick,
  innerRef,
  isSelected,
  buttonOnly = false,
  buttonTextOverride,
  onButtonClickOverride,
  alwaysSelectable,
}) => {
  const {
    distanceText,
    closeHourTodayText,
    isWithinOneHourOfCloseToday,
    curbsideOpen,
    driveThruOpen,
    diningRoomOpen,
    dineInOpen,
    takeoutOpen,
    selectRestaurant,
    isMobileOrderingAvailable,
    enableOrdering,
    areDiagnosticToolsEnabled,
    isRestaurantPosOnline,
    checkingItemAvailability,
  } = useStoreCard({ restaurant, shouldGetRemoteAvailabilityData: false });

  const { restaurantCanBeSelected } = useServiceModeStatus(restaurant);

  const { formatMessage } = useIntl();

  const showStoreServiceModes = useFlag(LaunchDarklyFlag.SHOW_STORE_SERVICE_MODES_SLIM_CARDS);
  const { region } = useLocale();
  /**
   * SETUP LOGIC FOR FORCING LOCALE CHANGE
   */
  const [showChangeRegionModal, setShowChangeRegionModal] = useState<boolean>(false);

  const handleChangeRegionModalDismiss = useCallback(() => {
    setShowChangeRegionModal(false);
  }, [setShowChangeRegionModal]);

  const changeRegionModalRegionFilter = useCallback(
    (regionForFilter: string) => {
      return (restaurant?.physicalAddress?.country || '').toUpperCase().startsWith(regionForFilter);
    },
    [restaurant]
  );

  const { current: restaurantAndUserRegionsMatch } = useRef(
    restaurantCountryMatchesRegion(restaurant, region)
  );

  const { current: defaultClickEventHandler } = useRef(
    determineDefaultOrderClickHandler({
      restaurantAndUserRegionsMatch,
      selectRestaurant,
      setShowChangeRegionModal,
    })
  );

  const { onOrderClick, orderButtonEnabledOverride, orderButtonText } = useOrderButtonProps({
    enableOrdering,
    isMobileOrderingAvailable,
    defaultClickEventHandler,
  });

  const storeTitle = restaurant.physicalAddress?.address1 || '';

  const handleOrderClick = useCallback(() => {
    if (onButtonClickOverride) {
      onButtonClickOverride(restaurant?._id ?? undefined);
      return;
    }
    onOrderClick(({
      ...restaurant,
      name: storeTitle,
      hasSelection: true,
    } as unknown) as StoreState);
  }, [onButtonClickOverride, onOrderClick, restaurant, storeTitle]);

  const enableOffers = useFlag(LaunchDarklyFlag.ENABLE_OFFERS);

  // Originally put into 'store-card' here:
  // https://github.com/rbilabs/EngineeringMonoRepo/pull/5284/files#diff-3b3d121668a85ccc2bbc1ad39e69a799R43-R46
  // This check is possibly a check for bad data from sanity and just trying to avoid rendering?
  // TODO: figure out why this check exists?
  if (!restaurant?.number || !restaurant?.physicalAddress?.address1) {
    return null;
  }

  const storeDetailsRoute = `${routes.store}/${restaurant._id}`;
  const storeDetailsText = formatMessage({ id: 'hoursAndInfo' });

  const closesInText = isWithinOneHourOfCloseToday
    ? formatMessage({ id: 'closesAt' }, { time: closeHourTodayText })
    : undefined;

  const availableServiceModes = showStoreServiceModes
    ? [
        dineInOpen && formatMessage({ id: 'dineIn' }),
        takeoutOpen && formatMessage({ id: 'takeOut' }),
        driveThruOpen && formatMessage({ id: 'driveThru' }),
        curbsideOpen && formatMessage({ id: 'curbside' }),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const orderButtonAriaLabel = formatMessage(
    { id: 'orderAtThisStore' },
    { storeLabel: storeTitle }
  );
  const selectStoreButtonAriaLabel = formatMessage(
    { id: 'selectThisStore' },
    { storeLabel: storeTitle }
  );
  const storeDetailsAriaLabel = formatMessage(
    { id: 'storeInfoAssistive' },
    { storeLabel: storeTitle }
  );

  const storeDiagnosticData: IStoreDiagnosticData = {
    driveThruOpen,
    diningRoomOpen,
    isRestaurantOpen: restaurantCanBeSelected,
    isMobileOrderingAvailable,
    isRestaurantPosOnline: !!isRestaurantPosOnline,
    restaurantName: restaurant.name || '',
    restaurantHasMobileOrdering: Boolean(restaurant.hasMobileOrdering),
    restaurantCountry: restaurant.physicalAddress.country || '',
    restaurantStreetAddress: restaurant.physicalAddress.address1 || '',
  };

  const isHalal = !!restaurant.isHalal;

  return (
    <>
      <StoreCardSlimView
        availableServiceModesText={availableServiceModes}
        storeTitle={storeTitle}
        isMobileOrderingAvailable={isMobileOrderingAvailable}
        onClick={onClick}
        innerRef={innerRef}
        storeDistance={distanceText}
        storeDetailsRoute={storeDetailsRoute}
        storeDetailsText={storeDetailsText}
        storeDetailsAriaLabel={storeDetailsAriaLabel}
        closesInText={closesInText}
        isSelected={isSelected}
        orderOrSelectButtonText={orderButtonText}
        orderButtonAriaLabel={orderButtonAriaLabel}
        selectStoreButtonAriaLabel={selectStoreButtonAriaLabel}
        storeAriaDescribedBy={restaurant.name as string}
        restaurantId={restaurant._id as string}
        storeNumber={restaurant.number}
        isRestaurantPosOnline={isRestaurantPosOnline}
        orderButtonEnabled={restaurantCanBeSelected || orderButtonEnabledOverride}
        onOrderOrSelectClick={handleOrderClick}
        enableOrdering={enableOrdering}
        areStoreDiagnosticsEnabled={areDiagnosticToolsEnabled}
        storeDiagnosticData={storeDiagnosticData}
        checkingItemAvailability={checkingItemAvailability}
        enableOffers={enableOffers}
        buttonOnly={buttonOnly}
        buttonTextOverride={buttonTextOverride}
        isHalal={isHalal}
        alwaysSelectable={alwaysSelectable}
      />
      {showChangeRegionModal && (
        <ChangeRegionModal
          redirectToCurrentLocation
          heading={formatMessage({ id: 'locatorChangeRegionHeading' })}
          regionFilter={changeRegionModalRegionFilter}
          onModalDismiss={handleChangeRegionModalDismiss}
          primaryText={formatMessage(
            { id: 'locatorChangeRegionPrimaryText' },
            { userRegion: region, restaurantRegion: getRestaurantCountry(restaurant) }
          )}
          secondaryText={formatMessage({ id: 'locatorChangeRegionSelection' })}
          disclaimer={formatMessage({ id: 'locatorChangeRegionDisclaimer' })}
        />
      )}
    </>
  );
};
